import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Button, Paper, Typography, IconButton, Tooltip, FormControl, InputLabel,
  Select, MenuItem, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, Card, CardContent, TextField, Autocomplete
} from '@mui/material';
import {
  Refresh as RefreshIcon, Print as PrintIcon, FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon, Delete as DeleteIcon, Add as AddIcon, Edit as EditIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';
import { useConfirm } from '../hooks/useConfirm';
import axiosInstance from '../services/axiosInstance';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { companyInfoService, CompanyInfo } from '../services/companyInfo.service';
import ConceptDialog from '../components/dailysales/ConceptDialog';
import '../styles/print.css';

interface Concept {
  conceptId: number;
  conceptCode: string;
  conceptName: string;
  conceptNameAr: string;
  branchId: number;
  branchName: string;
  branchNameAr: string;
  description?: string;
  isActive: boolean;
  displayOrder: number;
  createdDate: string;
}

interface Branch {
  branchId: number;
  branchName: string;
  branchNameAr: string;
}

const ConceptsPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const confirm = useConfirm();
  const printRef = useRef<HTMLDivElement>(null);
  const [concepts, setConcepts] = useState<Concept[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedConcept, setSelectedConcept] = useState<Concept | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  const t = {
    title: isRTL ? 'إدارة الكونسبتات' : 'Concepts Management',
    addConcept: isRTL ? 'إضافة كونسبت' : 'Add Concept',
    branch: isRTL ? 'الفرع' : 'Branch',
    allBranches: isRTL ? 'جميع الفروع' : 'All Branches',
    code: isRTL ? 'الكود' : 'Code',
    name: isRTL ? 'الاسم' : 'Name',
    description: isRTL ? 'الوصف' : 'Description',
    active: isRTL ? 'نشط' : 'Active',
    order: isRTL ? 'الترتيب' : 'Order',
    actions: isRTL ? 'الإجراءات' : 'Actions',
    yes: isRTL ? 'نعم' : 'Yes',
    no: isRTL ? 'لا' : 'No',
    noData: isRTL ? 'لا توجد بيانات' : 'No data available',
    refresh: isRTL ? 'تحديث' : 'Refresh',
    print: isRTL ? 'طباعة' : 'Print',
    exportExcel: isRTL ? 'تصدير Excel' : 'Export Excel',
    exportPDF: isRTL ? 'تصدير PDF' : 'Export PDF',
    edit: isRTL ? 'تعديل' : 'Edit',
    delete: isRTL ? 'حذف' : 'Delete',
    confirmDelete: isRTL ? 'هل أنت متأكد من حذف هذا الكونسبت؟' : 'Are you sure you want to delete this concept?',
    deleteSuccess: isRTL ? 'تم الحذف بنجاح' : 'Deleted successfully',
    rowsPerPage: isRTL ? 'عدد الصفوف:' : 'Rows per page:'
  };

  useEffect(() => { loadCompanyInfo(); loadBranches(); loadConcepts(); }, []);
  useEffect(() => { loadConcepts(); }, [selectedBranchId]);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) { console.error('Failed to load company info:', error); }
  };

  const loadBranches = async () => {
    try {
      const response = await axiosInstance.get('/Branches');
      setBranches(response.data);
    } catch (error) { console.error('Failed to load branches:', error); }
  };

  const loadConcepts = async () => {
    try {
      setLoading(true);
      const url = selectedBranchId ? `/Concepts/branch/${selectedBranchId}` : '/Concepts';
      const response = await axiosInstance.get(url);
      setConcepts(response.data);
    } catch (error: any) {
      toast.error(isRTL ? 'فشل تحميل الكونسبتات' : 'Failed to load concepts');
    } finally { setLoading(false); }
  };

  const handleAdd = () => { setSelectedConcept(null); setDialogOpen(true); };
  const handleEdit = (concept: Concept) => { setSelectedConcept(concept); setDialogOpen(true); };
  const handleDialogClose = (refresh?: boolean) => {
    setDialogOpen(false);
    setSelectedConcept(null);
    if (refresh) loadConcepts();
  };

  const handleDelete = async (conceptId: number) => {
    if (!(await confirm({ message: t.confirmDelete, severity: 'error' }))) return;
    try {
      await axiosInstance.delete(`/Concepts/${conceptId}`);
      toast.success(t.deleteSuccess);
      loadConcepts();
    } catch (error: any) {
      toast.error(error.response?.data?.message || (isRTL ? 'فشل حذف الكونسبت' : 'Failed to delete concept'));
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => { setPage(newPage); };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const exportToExcel = async () => {
    if (concepts.length === 0) {
      toast.error(isRTL ? 'لا توجد بيانات للتصدير' : 'No data to export');
      return;
    }
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(isRTL ? 'الكونسبتات' : 'Concepts');
      let currentRow = 1;
      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({ base64: companyInfo.logoBase64, extension: 'png' });
          worksheet.addImage(logoImage, { tl: { col: 0, row: 0 }, ext: { width: 80, height: 60 } });
        } catch (err) { console.warn('Could not add logo:', err); }
      }
      worksheet.mergeCells('B1:G1');
      const companyCell = worksheet.getCell('B1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 18, bold: true, color: { argb: 'FF00796B' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.mergeCells('B2:G2');
      const addressCell = worksheet.getCell('B2');
      addressCell.value = `📍 ${companyInfo?.address || ''}`;
      addressCell.font = { size: 9, color: { argb: 'FF424242' } };
      addressCell.alignment = { horizontal: 'center' };
      worksheet.mergeCells('B3:G3');
      const contactCell = worksheet.getCell('B3');
      contactCell.value = `📞 ${companyInfo?.phone || ''} | ✉️ ${companyInfo?.email || ''}`;
      contactCell.font = { size: 9, color: { argb: 'FF424242' } };
      contactCell.alignment = { horizontal: 'center' };
      currentRow = 5;
      worksheet.mergeCells(`A${currentRow}:G${currentRow}`);
      const titleCell = worksheet.getCell(`A${currentRow}`);
      titleCell.value = isRTL ? 'تقرير الكونسبتات' : 'Concepts Report';
      titleCell.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF00796B' } };
      worksheet.getRow(currentRow).height = 25;
      currentRow++;
      worksheet.mergeCells(`A${currentRow}:G${currentRow}`);
      const dateCell = worksheet.getCell(`A${currentRow}`);
      const selectedBranchName = selectedBranchId
        ? branches.find((b) => b.branchId === selectedBranchId)?.[isRTL ? 'branchNameAr' : 'branchName']
        : (isRTL ? 'جميع الفروع' : 'All Branches');
      dateCell.value = `${isRTL ? 'التاريخ:' : 'Date:'} ${new Date().toLocaleDateString(isRTL ? 'ar-EG' : 'en-GB')} | ${isRTL ? 'الفرع:' : 'Branch:'} ${selectedBranchName}`;
      dateCell.font = { size: 10, bold: true, color: { argb: 'FF00796B' } };
      dateCell.alignment = { horizontal: 'center' };
      currentRow += 2;
      const summaryRow = currentRow;
      worksheet.mergeCells(`A${summaryRow}:C${summaryRow}`);
      const totalCell = worksheet.getCell(`A${summaryRow}`);
      totalCell.value = `${isRTL ? 'إجمالي الكونسبتات:' : 'Total Concepts:'} ${concepts.length}`;
      totalCell.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
      totalCell.alignment = { horizontal: 'center', vertical: 'middle' };
      totalCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4CAF50' } };
      worksheet.getRow(summaryRow).height = 25;
      worksheet.mergeCells(`D${summaryRow}:G${summaryRow}`);
      const activeCell = worksheet.getCell(`D${summaryRow}`);
      const activeCount = concepts.filter(c => c.isActive).length;
      activeCell.value = `${isRTL ? 'النشط:' : 'Active:'} ${activeCount} | ${isRTL ? 'غير نشط:' : 'Inactive:'} ${concepts.length - activeCount}`;
      activeCell.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
      activeCell.alignment = { horizontal: 'center', vertical: 'middle' };
      activeCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
      currentRow += 2;
      const headerRow = worksheet.getRow(currentRow);
      headerRow.values = [isRTL ? 'الكود' : 'Code', isRTL ? 'الاسم' : 'Name', isRTL ? 'الفرع' : 'Branch', isRTL ? 'الوصف' : 'Description', isRTL ? 'نشط' : 'Active', isRTL ? 'الترتيب' : 'Order', isRTL ? 'تاريخ الإنشاء' : 'Created Date'];
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF00796B' } };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
      headerRow.height = 25;
      headerRow.eachCell((cell) => {
        cell.border = { top: { style: 'thin', color: { argb: 'FF000000' } }, bottom: { style: 'thin', color: { argb: 'FF000000' } }, left: { style: 'thin', color: { argb: 'FF000000' } }, right: { style: 'thin', color: { argb: 'FF000000' } } };
      });
      currentRow++;
      concepts.forEach((concept, index) => {
        const row = worksheet.getRow(currentRow);
        row.values = [concept.conceptCode, isRTL ? concept.conceptNameAr : concept.conceptName, isRTL ? concept.branchNameAr : concept.branchName, concept.description || '-', concept.isActive ? (isRTL ? 'نعم' : 'Yes') : (isRTL ? 'لا' : 'No'), concept.displayOrder, new Date(concept.createdDate).toLocaleDateString(isRTL ? 'ar-EG' : 'en-GB')];
        row.alignment = { horizontal: 'center', vertical: 'middle' };
        row.height = 20;
        row.eachCell((cell) => {
          cell.border = { top: { style: 'thin', color: { argb: 'FFD3D3D3' } }, bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } }, left: { style: 'thin', color: { argb: 'FFD3D3D3' } }, right: { style: 'thin', color: { argb: 'FFD3D3D3' } } };
          if (index % 2 === 0) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
          }
        });
        currentRow++;
      });
      worksheet.columns = [{ width: 15 }, { width: 30 }, { width: 25 }, { width: 35 }, { width: 12 }, { width: 12 }, { width: 15 }];
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Concepts_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(t.exportExcel + ' ✓');
    } catch (error) {
      console.error('Excel export failed:', error);
      toast.error(isRTL ? 'فشل التصدير إلى Excel' : 'Excel export failed');
    }
  };

  const exportToPdf = async () => {
    if (!companyInfo || concepts.length === 0) {
      toast.error(isRTL ? 'لا توجد بيانات للتصدير' : 'No data to export');
      return;
    }
    try {
      toast.info(isRTL ? 'جاري إنشاء PDF...' : 'Generating PDF...');
      const selectedBranchName = selectedBranchId
        ? branches.find((b) => b.branchId === selectedBranchId)?.[isRTL ? 'branchNameAr' : 'branchName']
        : (isRTL ? 'جميع الفروع' : 'All Branches');
      const activeCount = concepts.filter(c => c.isActive).length;
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.width = '210mm';
      tempDiv.style.background = 'white';
      tempDiv.style.padding = '15mm';
      tempDiv.style.fontFamily = 'Arial, sans-serif';
      tempDiv.style.direction = isRTL ? 'rtl' : 'ltr';
      tempDiv.innerHTML = `
        <div style="text-align: center; border-bottom: 3px solid #00796B; padding-bottom: 10px; margin-bottom: 15px;">
          ${companyInfo.logoBase64 ? `<img src="${companyInfo.logoBase64}" style="width: 80px; height: 60px; margin-bottom: 10px;" alt="Logo">` : ''}
          <div style="font-size: 24px; font-weight: bold; color: #00796B;">${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</div>
          <div style="font-size: 11px; color: #666; margin-top: 3px;">${companyInfo.address || ''}</div>
          <div style="font-size: 11px; color: #666;">${companyInfo.phone || ''} | ${companyInfo.email || ''}</div>
        </div>
        <div style="background: #00796B; color: white; padding: 8px; text-align: center; font-size: 18px; font-weight: bold; margin: 15px 0;">
          ${isRTL ? 'تقرير الكونسبتات' : 'Concepts Report'}
        </div>
        <div style="text-align: center; margin-bottom: 15px; font-size: 11px; color: #666;">
          ${isRTL ? 'التاريخ:' : 'Date:'} ${new Date().toLocaleDateString(isRTL ? 'ar-EG' : 'en-GB')} | ${isRTL ? 'الفرع:' : 'Branch:'} ${selectedBranchName}
        </div>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin: 15px 0;">
          <div style="border: 2px solid #4CAF50; padding: 10px; text-align: center; border-radius: 5px; background: #E8F5E9;">
            <div style="font-size: 11px; color: #2E7D32; margin-bottom: 5px;">${isRTL ? 'إجمالي الكونسبتات' : 'Total Concepts'}</div>
            <div style="font-size: 20px; font-weight: bold; color: #2E7D32;">${concepts.length}</div>
          </div>
          <div style="border: 2px solid #1976D2; padding: 10px; text-align: center; border-radius: 5px; background: #E3F2FD;">
            <div style="font-size: 11px; color: #1565C0; margin-bottom: 5px;">${isRTL ? 'النشط / غير النشط' : 'Active / Inactive'}</div>
            <div style="font-size: 20px; font-weight: bold; color: #1565C0;">${activeCount} / ${concepts.length - activeCount}</div>
          </div>
        </div>
        <table style="width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 10px;">
          <thead>
            <tr style="background: #00796B; color: white;">
              <th style="padding: 8px 5px; text-align: ${isRTL ? 'right' : 'left'}; border: 1px solid #00796B;">${isRTL ? 'الكود' : 'Code'}</th>
              <th style="padding: 8px 5px; text-align: ${isRTL ? 'right' : 'left'}; border: 1px solid #00796B;">${isRTL ? 'الاسم' : 'Name'}</th>
              <th style="padding: 8px 5px; text-align: ${isRTL ? 'right' : 'left'}; border: 1px solid #00796B;">${isRTL ? 'الفرع' : 'Branch'}</th>
              <th style="padding: 8px 5px; text-align: ${isRTL ? 'right' : 'left'}; border: 1px solid #00796B;">${isRTL ? 'الوصف' : 'Description'}</th>
              <th style="padding: 8px 5px; text-align: center; border: 1px solid #00796B;">${isRTL ? 'نشط' : 'Active'}</th>
              <th style="padding: 8px 5px; text-align: center; border: 1px solid #00796B;">${isRTL ? 'الترتيب' : 'Order'}</th>
            </tr>
          </thead>
          <tbody>
            ${concepts.map((concept, idx) => `
              <tr style="background: ${idx % 2 === 0 ? '#FFFFFF' : '#F5F5F5'};">
                <td style="padding: 6px 5px; border: 1px solid #E0E0E0; text-align: ${isRTL ? 'right' : 'left'}; color: #000;">${concept.conceptCode}</td>
                <td style="padding: 6px 5px; border: 1px solid #E0E0E0; text-align: ${isRTL ? 'right' : 'left'}; color: #000;">${isRTL ? concept.conceptNameAr : concept.conceptName}</td>
                <td style="padding: 6px 5px; border: 1px solid #E0E0E0; text-align: ${isRTL ? 'right' : 'left'}; color: #000;">${isRTL ? concept.branchNameAr : concept.branchName}</td>
                <td style="padding: 6px 5px; border: 1px solid #E0E0E0; text-align: ${isRTL ? 'right' : 'left'}; color: #000;">${concept.description || '-'}</td>
                <td style="padding: 6px 5px; border: 1px solid #E0E0E0; text-align: center; font-weight: bold; color: ${concept.isActive ? '#2E7D32' : '#C62828'};">${concept.isActive ? (isRTL ? 'نعم' : 'Yes') : (isRTL ? 'لا' : 'No')}</td>
                <td style="padding: 6px 5px; border: 1px solid #E0E0E0; text-align: center; color: #000;">${concept.displayOrder}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
      document.body.appendChild(tempDiv);
      const canvas = await html2canvas(tempDiv, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }
      pdf.save(`Concepts_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.removeChild(tempDiv);
      toast.success(t.exportPDF + ' ✓');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error(isRTL ? 'فشل التصدير إلى PDF' : 'PDF export failed');
    }
  };

  const handlePrint = () => { window.print(); };

  const paginatedConcepts = concepts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const selectedBranchName = selectedBranchId
    ? branches.find((b) => b.branchId === selectedBranchId)?.[isRTL ? 'branchNameAr' : 'branchName']
    : (isRTL ? 'جميع الفروع' : 'All Branches');

  return (
    <Box sx={{ p: 3 }}>
      <Card className="no-print" sx={{ mb: 3, bgcolor: '#00796B', color: 'white' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h4" fontWeight="bold">📊 {t.title}</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Tooltip title={t.refresh}><IconButton sx={{ color: 'white' }} onClick={loadConcepts}><RefreshIcon /></IconButton></Tooltip>
              <Tooltip title={t.exportExcel}><IconButton sx={{ color: 'white' }} onClick={exportToExcel}><ExcelIcon /></IconButton></Tooltip>
              <Tooltip title={t.exportPDF}><IconButton sx={{ color: 'white' }} onClick={exportToPdf}><PdfIcon /></IconButton></Tooltip>
              <Tooltip title={t.print}><IconButton sx={{ color: 'white' }} onClick={handlePrint}><PrintIcon /></IconButton></Tooltip>
              <Button variant="contained" sx={{ bgcolor: 'white', color: '#00796B', '&:hover': { bgcolor: 'grey.100' } }} startIcon={<AddIcon />} onClick={handleAdd}>{t.addConcept}</Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
      <Paper className="no-print" sx={{ p: 2, mb: 3 }}>
        <Autocomplete
          size="small"
          options={branches}
          getOptionLabel={(o) => isRTL ? (o.branchNameAr || o.branchName) : o.branchName}
          value={branches.find(x => x.branchId === selectedBranchId) || null}
          onChange={(_, v) => setSelectedBranchId(v?.branchId || '')}
          renderInput={(params) => <TextField {...params} label={t.branch} />}
          sx={{ minWidth: 250 }}
        />
      </Paper>
      <Box ref={printRef} id="concepts-report-content">
        <Box sx={{ display: 'none' }} className="print-only">
          <Box sx={{ textAlign: 'center', mb: 2, pb: 1, borderBottom: '3px solid #00796B' }}>
            {companyInfo?.logoBase64 && (<img src={companyInfo.logoBase64} alt="Logo" style={{ width: 80, height: 60, marginBottom: 8 }} />)}
            <Typography variant="h4" sx={{ color: '#00796B', fontWeight: 'bold', fontSize: '20px' }}>{isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}</Typography>
            <Typography variant="body2" sx={{ fontSize: '10px', color: '#666' }}>{companyInfo?.address}</Typography>
            <Typography variant="body2" sx={{ fontSize: '10px', color: '#666' }}>{companyInfo?.phone} | {companyInfo?.email}</Typography>
          </Box>
          <Box sx={{ bgcolor: '#00796B', color: 'white', py: 0.5, px: 1, textAlign: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '16px' }}>{isRTL ? 'تقرير الكونسبتات' : 'Concepts Report'}</Typography>
          </Box>
          <Box sx={{ textAlign: 'center', mb: 1 }}>
            <Typography variant="body2" sx={{ fontSize: '9px', color: '#666' }}>{isRTL ? 'التاريخ:' : 'Date:'} {new Date().toLocaleDateString(isRTL ? 'ar-EG' : 'en-GB')} | {isRTL ? 'الفرع:' : 'Branch:'} {selectedBranchName}</Typography>
          </Box>
        </Box>
        <TableContainer component={Paper}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}><CircularProgress sx={{ color: '#00796B' }} /></Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#00796B' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.code}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.name}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.branch}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.description}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t.active}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t.order}</TableCell>
                    <TableCell className="no-print" sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t.actions}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedConcepts.map((concept) => (
                    <TableRow key={concept.conceptId} hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                      <TableCell>{concept.conceptCode}</TableCell>
                      <TableCell>{isRTL ? concept.conceptNameAr : concept.conceptName}</TableCell>
                      <TableCell>{isRTL ? concept.branchNameAr : concept.branchName}</TableCell>
                      <TableCell>{concept.description || '-'}</TableCell>
                      <TableCell align="center"><Chip label={concept.isActive ? t.yes : t.no} color={concept.isActive ? 'success' : 'error'} size="small" /></TableCell>
                      <TableCell align="center">{concept.displayOrder}</TableCell>
                      <TableCell className="no-print" align="center">
                        <Tooltip title={t.edit}><IconButton size="small" color="primary" onClick={() => handleEdit(concept)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title={t.delete}><IconButton size="small" color="error" onClick={() => handleDelete(concept.conceptId)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {paginatedConcepts.length === 0 && (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><Typography variant="body1" color="text.secondary">{t.noData}</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination className="no-print" rowsPerPageOptions={[10, 25, 50, 100]} component="div" count={concepts.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} labelRowsPerPage={t.rowsPerPage} />
            </>
          )}
        </TableContainer>
      </Box>
      {dialogOpen && (<ConceptDialog open={dialogOpen} concept={selectedConcept} onClose={handleDialogClose} />)}
    </Box>
  );
};

export default ConceptsPage;